import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useBreakpointValue } from '@salesforce/retail-react-app/app/components/shared/ui'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { useClaraPreferences } from './use-clara-preferences'
import { loadOnInteractionOrIdle } from '../utils/load-on-interaction'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    DEFAULT_INTL_REDIRECT_HOSTS,
    normalizeHostname,
    parseHostList
} from '../utils/intl-default-locale-redirect'

/**
 * Intl hosts that should use claraConfig.intl.
 * Prefers BM/MRT INTL_DEFAULT_LOCALE_HOSTS (same list as locale redirect); falls back to code default.
 */
export const getIntlClaraHosts = (appConfig) => {
    const config = appConfig || getConfig()?.app || {}
    return parseHostList(config.INTL_DEFAULT_LOCALE_HOSTS, DEFAULT_INTL_REDIRECT_HOSTS)
}

/**
 * Pick ESD block from BM claraConfig by storefront host.
 * Intl host → claraConfig.intl (fallback default). All other hosts → default.
 */
export const getClaraConfigForHost = (claraConfig, hostname, intlHosts) => {
    const hosts = intlHosts || getIntlClaraHosts()
    const host = normalizeHostname(hostname)
    const isIntlHost = Boolean(host) && hosts.includes(host)

    if (isIntlHost && claraConfig?.intl) {
        return claraConfig.intl
    }

    return claraConfig?.default || null
}

const useClaraChat = ({ enabled = true } = {}) => {
    const { claraConfig, isLoading } = useClaraPreferences()
    const location = useLocation()
    const isDesktop = useBreakpointValue({ base: false, lg: true })
    const { locale } = useMultiSite()
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const selectedConfig = getClaraConfigForHost(claraConfig, hostname)

    // Effect to initialize Clara Chat once
    useEffect(() => {
        const config = getClaraConfigForHost(
            claraConfig,
            typeof window !== 'undefined' ? window.location.hostname : ''
        )
        if (isLoading || !config) return

        const SCRIPT_ID = 'clara-bootstrap-script'

        const brand = 'garage'
        const userCountry = locale.isSupportedByESW ? locale.countryCode : 'GB'
        const userLanguage = 'English'
        const chatMedium = 'Web Chat'

        // This observer is used for accessibility. If the iframe which is loaded async is not visible add aria-hidden true
        const observeIframes = () => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeName === 'IFRAME') {
                            if (node.id === 'embeddedMessagingSiteContextFrame') {
                                const style = window.getComputedStyle(node)
                                if (style?.display === 'none') {
                                    node.setAttribute('aria-hidden', 'true')
                                }
                            }
                        }
                    })
                })
            })

            observer.observe(document.body, {
                childList: true,
                subtree: true
            })

            return observer
        }

        const onEmbeddedMessagingReady = () => {
            try {
                window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
                    f_Language: userLanguage,
                    Chat_Medium: chatMedium,
                    f_BrandName: brand,
                    f_Country: userCountry
                })
            } catch (err) {
                console.error('Error setting hidden prechat fields:', err)
            }
        }

        const onEmbeddedMessagingWindowMaximized = () => {
            // Apply z-index to chat elements
            const element = document.getElementById('embeddedMessagingFrame')
            if (element) {
                element.style.zIndex = '9999'
            }
        }

        const onEmbeddedMessagingWindowMinimized = () => {
            // Apply position styles when chat is minimized
            const element = document.getElementById('embeddedMessagingFrame')
            if (element) {
                // Check if we're on a PDP (Product Detail Page)
                const isPDP = window.location.pathname.match(/\/p\//)
                element.style.bottom = isPDP ? '118px' : '24px'
                element.style.right = isDesktop ? '24px' : '16px'
            }
        }

        const initEmbeddedMessaging = () => {
            try {
                const bootstrap = window.embeddedservice_bootstrap

                if (!bootstrap) return

                window.addEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)
                window.addEventListener(
                    'onEmbeddedMessagingWindowMaximized',
                    onEmbeddedMessagingWindowMaximized
                )
                window.addEventListener(
                    'onEmbeddedMessagingWindowMinimized',
                    onEmbeddedMessagingWindowMinimized
                )

                bootstrap.settings.language = 'en'
                bootstrap.init(config.OrganizationId, config.DeveloperName, config.InitUrl, {
                    scrt2URL: config.Url
                })
            } catch (err) {
                console.error('Error initializing Clara Chat:', err)
            }
        }

        const loadScript = () => {
            if (document.getElementById(SCRIPT_ID)) return

            const script = document.createElement('script')
            script.id = SCRIPT_ID
            script.src = config.ScriptUrl
            script.onload = initEmbeddedMessaging
            script.onerror = () => console.error('Failed to load Clara bootstrap script')

            document.head.appendChild(script)
        }

        const iframeObserver = observeIframes()

        // Defer Clara Chat loading until user interaction or idle
        // This improves TTI by not blocking the main thread on initial load
        const interactionCleanup = loadOnInteractionOrIdle(() => {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', loadScript)
            } else {
                loadScript()
            }
        }, { timeout: 3000 })

        return () => {
            interactionCleanup?.()
            document.removeEventListener('DOMContentLoaded', loadScript)
            window.removeEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)
            window.removeEventListener(
                'onEmbeddedMessagingWindowMaximized',
                onEmbeddedMessagingWindowMaximized
            )
            window.removeEventListener(
                'onEmbeddedMessagingWindowMinimized',
                onEmbeddedMessagingWindowMinimized
            )
            iframeObserver?.disconnect()
        }
    }, [claraConfig, isLoading])

    // Separate effect to control visibility based on enabled prop
    useEffect(() => {
        if (isLoading) return

        // Use a small delay to ensure the chat button is initialized
        const timer = setTimeout(() => {
            try {
                const bootstrap = window.embeddedservice_bootstrap

                if (!bootstrap?.utilAPI) return

                if (enabled) {
                    // Show chat button when enabled
                    bootstrap.utilAPI.showChatButton()
                } else {
                    // Hide chat button when disabled (e.g., on checkout page)
                    bootstrap.utilAPI.hideChatButton()
                }
            } catch (err) {
                console.error('Error toggling Clara Chat visibility:', err)
            }
        }, 100)

        return () => clearTimeout(timer)
    }, [enabled, isLoading])

    // Effect to update chat position when route changes or breakpoint changes
    useEffect(() => {
        const updateChatPosition = () => {
            const element = document.getElementById('embeddedMessagingFrame')
            // Only apply position styles if the chat is minimized
            if (element && element.classList.contains('isMinimized')) {
                const isPDP = location.pathname.match(/\/p\//)
                element.style.bottom = isPDP ? '118px' : '24px'
                element.style.right = isDesktop ? '24px' : '16px'
            }
        }

        // Update immediately
        updateChatPosition()

        // Also update after a small delay to ensure the element exists
        const timer = setTimeout(updateChatPosition, 100)

        return () => clearTimeout(timer)
    }, [location.pathname, isDesktop])

    return {
        isReady: !isLoading && !!selectedConfig
    }
}

/**
 * Programmatically opens Clara Chat by clicking the embedded messaging button.
 * Reuses the same approach as checkout-header.jsx.
 */
export const openClaraChat = (e) => {
    e?.preventDefault()
    const chatButton = document.getElementById('embeddedMessagingConversationButton')
    if (chatButton) {
        chatButton.click()
    }
}

export { useClaraChat }
